/**
 * Domains of personal/free email for validation (ex.: require corporate email).
 * Case-insensitive comparison via normalizeEmailDomain.
 */
export const PERSONAL_EMAIL_DOMAINS = new Set<string>([
  'aol.com',
  'aol.com.br',
  'bol.com.br',
  'gmail.com',
  'gmail.com.br',
  'globo.com',
  'gmx.com',
  'gmx.com.br',
  'googlemail.com',
  'googlemail.com.br',
  'hotmail.com',
  'hotmail.com.br',
  'icloud.com',
  'icloud.com.br',
  'ig.com.br',
  'itelefonica.com.br',
  'live.com',
  'live.com.br',
  'mac.com',
  'mac.com.br',
  'me.com',
  'me.com.br',
  'oi.com.br',
  'outlook.com',
  'outlook.com.br',
  'proton.com.br',
  'proton.me',
  'protonmail.com',
  'protonmail.com.br',
  'r7.com',
  'superig.com.br',
  'terra.com.br',
  'tuta.com',
  'tuta.com.br',
  'tutanota.com',
  'tutanota.com.br',
  'uol.com.br',
  'yahoo.com',
  'yahoo.com.br',
  'yandex.com',
  'yandex.com.br',
  'yandex.ru',
  'zipmail.com.br',
  'zoho.com',
  'zoho.com.br',
]);

/**
 * Returns the domain in lowercase from a complete email or only the domain.
 */
export function normalizeEmailDomain(emailOrDomain: string): string {
  const trimmed = emailOrDomain.trim().toLowerCase();
  const at = trimmed.lastIndexOf('@');
  return at >= 0 ? trimmed.slice(at + 1) : trimmed;
}

/** true if the email domain is in the list of personal/free domains. */
export function isPersonalEmailDomain(emailOrDomain: string): boolean {
  return PERSONAL_EMAIL_DOMAINS.has(normalizeEmailDomain(emailOrDomain));
}

/** First email from the list whose domain is not personal/free (already validated by the caller). */
export function pickFirstNonPersonalEmail(validEmails: ReadonlyArray<string>): string | null {
  const found = validEmails.find((email) => !isPersonalEmailDomain(email));
  return found ?? null;
}

/**
 * All non-personal (corporate) emails, trimmed, de-duplicated by case-insensitive domain+local.
 */
export function listCorporateEmails(validEmails: ReadonlyArray<string>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of validEmails) {
    const email = raw.trim();
    if (!email || isPersonalEmailDomain(email)) continue;
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(email);
  }
  return out;
}
